import { useState, useEffect } from 'react';

/**
 * Hook to manage PWA Installation events.
 * Listens for 'beforeinstallprompt' and provides a trigger function.
 *
 * Browser support note: 'beforeinstallprompt' only fires on Chromium-based browsers (Chrome/
 * Edge on Android, desktop Chrome). It never fires on iOS Safari, so on iOS `isInstallable`
 * stays false even though iOS supports "Add to Home Screen" manually — callers (InstallBanner)
 * are expected to show separate manual instructions for iOS using the `isMobile` flag rather
 * than relying on `isInstallable` alone.
 */
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

    useEffect(() => {
    // Detect mobile device
    const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    
    setIsMobile(mobileCheck);
    
    // If not mobile OR already standalone (installed), don't show the banner
    if (!mobileCheck || isStandalone) {
      setIsInstallable(false);
      return;
    }

    const handler = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log('[PWA] beforeinstallprompt event captured');
    };

    window.addEventListener('beforeinstallprompt', handler);

    // If already installed, don't show anything
    if (isStandalone) {
      setIsInstallable(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // If we don't have a prompt (likely HTTP/iOS), return null to handle as manual guide
      return null;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`[PWA] User response to install prompt: ${outcome}`);

    // We've used the prompt, and can't use it again
    setDeferredPrompt(null);
    setIsInstallable(false);
    return outcome;
  };

  return { isInstallable, isMobile, handleInstall, setIsInstallable };
};
